import React, { memo, useState } from 'react';
import Button from '@mui/material/Button';
import Popover from '@mui/material/Popover';
import {
  EVENTS_AND_WEBINARS_LINKS,
  LINKS,
  PAGE_PATHS
} from '../../constants/constants';
import styles from './user-buttons.module.scss';
import Link from 'next/link';
import { Badge } from '@mui/material';

type UserButtonsProps = {
  isAuthenticated: boolean;
  userMenuOpen: boolean;
  handleUserMenuOpen: (event: React.MouseEvent<HTMLButtonElement>) => void;
  showWelcomeHighlight?: boolean;
};

type FeatureButtonsProps = {
  onClick?: React.MouseEventHandler;
};

export const FeatureButtons = ({ onClick }: FeatureButtonsProps) => (
  <>
    <a
      href={LINKS.advertise}
      target='_blank'
      className={`theme-btn auth-btn basic-btn ${styles.btn}`}
    >
      Advertise
    </a>
    <a
      className={`theme-btn auth-btn basic-btn ${styles.btn}`}
      href={EVENTS_AND_WEBINARS_LINKS.EVENTS}
      target='_blank'
    >
      Events
    </a>
    <Link
      prefetch={false}
      className={`theme-btn auth-btn basic-btn ${styles.btn}`}
      href={EVENTS_AND_WEBINARS_LINKS.ON_DEMAND}
      onClick={onClick}
    >
      Webinars
    </Link>
    <a
      href='https://urbanity.theurbandeveloper.com'
      target='_blank'
      className={`theme-btn auth-btn basic-btn ${styles.btn}`}
    >
      Urbanity
    </a>
    <span className={styles.hideOnDesktop}>
      <a
        href='https://events.theurbandeveloper.com/tud-awards/'
        target='_blank'
        className={`theme-btn auth-btn basic-btn ${styles.btn}`}
      >
        <div
          className='icon'
          style={{ backgroundImage: 'url(/static/imgs/icons/awards.svg)' }}
        />
        Industry Excellence
      </a>
    </span>
    <span className={styles.hideOnDesktop}>
      <a
        href='https://events.theurbandeveloper.com/ula/'
        target='_blank'
        className={`theme-btn auth-btn basic-btn ${styles.btn}`}
      >
        <div
          className='icon'
          style={{ backgroundImage: 'url(/static/imgs/icons/ula-awards.svg)' }}
        />
        Urban Leader
      </a>
    </span>
    <span className={styles.hideOnMobile}>
      <AwardsMenuDropdown />
    </span>
  </>
);

// Awards Dropdown using MUI Popover
const AwardsMenuDropdown = () => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (anchorEl === null) {
      setAnchorEl(event.currentTarget as HTMLButtonElement);
    } else {
      setAnchorEl(null);
    }
  };
  const handleClose = () => setAnchorEl(null);
  const open = Boolean(anchorEl);
  const id = open ? 'awards-popover' : undefined;

  return (
    <div style={{ display: 'inline-block' }}>
      <Button
        aria-describedby={id}
        variant='contained'
        color='primary'
        onClick={handleClick}
        className={`theme-btn auth-btn basic-btn ${styles.btn}`}
        style={{ display: 'flex', alignItems: 'center', gap: 8 }}
      >
        Awards
        <span style={{ fontSize: 12 }}>▼</span>
      </Button>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        className={styles.popover}
        sx={{ padding: 0 }}
        style={{ zIndex: 9999, padding: 0 }}
        PaperProps={{
          sx: {
            minWidth: 220,
            maxWidth: 220,
            width: 220,
            p: 0,
            background: '#fff',
            border: '1px solid #e0e0e0',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            borderRadius: 2
          }
        }}
      >
        <ul
          style={{ margin: 0, padding: 0, minWidth: 220, background: '#fff' }}
        >
          <li style={{ listStyle: 'none' }}>
            <a
              href='https://events.theurbandeveloper.com/tud-awards/'
              target='_blank'
              rel='noopener noreferrer'
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '12px 24px',
                textDecoration: 'none',
                color: '#222',
                textAlign: 'left',
                fontWeight: 500,
                fontSize: 16,
                transition: 'background 0.2s',
                justifyContent: 'flex-start'
              }}
            >
              <span
                className='icon'
                style={{
                  backgroundImage: 'url(/static/imgs/icons/awards.svg)',
                  backgroundRepeat: 'no-repeat',
                  width: 20,
                  height: 20,
                  display: 'inline-block',
                  backgroundSize: 'contain'
                }}
              />
              Industry Excellence
            </a>
          </li>
          <li style={{ listStyle: 'none' }}>
            <a
              href='https://events.theurbandeveloper.com/ula/'
              target='_blank'
              rel='noopener noreferrer'
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '12px 24px',
                textDecoration: 'none',
                color: '#222',
                textAlign: 'left',
                fontWeight: 500,
                fontSize: 16,
                transition: 'background 0.2s',
                justifyContent: 'flex-start'
              }}
            >
              <span
                className='icon'
                style={{
                  backgroundImage: 'url(/static/imgs/icons/ula-awards.svg)',
                  backgroundRepeat: 'no-repeat',
                  width: 20,
                  height: 20,
                  display: 'inline-block',
                  backgroundSize: 'contain'
                }}
              />
              Urban Leader
            </a>
          </li>
        </ul>
      </Popover>
    </div>
  );
};

const UserButtons = ({
  isAuthenticated,
  userMenuOpen,
  handleUserMenuOpen,
  showWelcomeHighlight
}: UserButtonsProps) => {
  return (
    <div className={styles.btnsWrap}>
      <FeatureButtons />
      {!isAuthenticated ? (
        <>
          <Link
            prefetch={false}
            href={PAGE_PATHS.signin}
            className={`theme-btn auth-btn signin-btn outline ${styles.btn}`}
          >
            Sign In
          </Link>
          <Link
            prefetch={false}
            href={PAGE_PATHS.membershipPlans}
            className={`theme-btn auth-btn signup-btn ${styles.btn}`}
          >
            <div
              className='icon'
              style={{
                backgroundImage: 'url(/static/imgs/icons/membership-dark.svg)',
                backgroundSize: 'contain',
                backgroundRepeat: 'no-repeat'
              }}
            />
            Membership
          </Link>
        </>
      ) : (
        <Badge
          color='primary'
          overlap='circular'
          variant='dot'
          invisible={!showWelcomeHighlight}
        >
          <Button
            aria-owns={userMenuOpen ? 'user-menu' : undefined}
            aria-haspopup='true'
            style={{
              borderRadius: 3,
              height: 47,
              display: 'block',
              justifyContent: 'center',
              alignItems: 'center',
              border: 0,
              minWidth: 47,
              padding: 0,
              backgroundColor: 'transparent'
            }}
            className='header__top-btn header__top-btn--active'
            onClick={handleUserMenuOpen}
          >
            <span className='material-symbols-outlined'>account_circle</span>
            {/* <UserIcon color={isHovered ? "#F4A71C" : "#000000"} /> */}
          </Button>
        </Badge>
      )}
    </div>
  );
};

export default UserButtons;
